import {
  Components,
  Palette,
  Theme,
  alpha,
  buttonClasses,
} from "@mui/material";

// Add "link" variant to Button variant options
declare module "@mui/material/Button" {
  interface ButtonClasses {
    link: string;
    linkPrimary: string;
    linkSecondary: string;
    linkSuccess: string;
    linkError: string;
    linkInfo: string;
    linkWarning: string;
    linkSizeSmall: string;
    linkSizeMedium: string;
    linkSizeLarge: string;
    linkSizeInherit: string;

    sizeInherit: string;
  }
}

const getContainedColor =
  (colorName: keyof Palette) =>
  ({ theme }: { theme: Theme }) => ({
    color: theme.palette.common.white,
    // @ts-ignore: Object can be undefined error
    backgroundColor: theme.palette[colorName][600],
    // @ts-ignore: Object can be undefined error
    boxShadow: `0px 0px 2px ${theme.palette[colorName][600]}`,
    "&:hover:not(:disabled), &:focus:not(:disabled)": {
      color: theme.palette.common.white,
      // @ts-ignore: Object can be undefined error
      backgroundColor: theme.palette[colorName][800],
    },
    "&:active:not(:disabled)": {
      color: theme.palette.common.white,
      // @ts-ignore: Object can be undefined error
      backgroundColor: theme.palette[colorName][900],
    },
  });

const getOutlinedColor =
  (colorName: keyof Palette) =>
  ({ theme }: { theme: Theme }) => ({
    // @ts-ignore: Object can be undefined error
    backgroundColor: theme.palette.common.white,
    // @ts-ignore: Object can be undefined error
    borderColor: theme.palette[colorName][300],
    // @ts-ignore: Object can be undefined error
    color: theme.palette[colorName][700],
    "&:hover:not(:disabled), &:focus:not(:disabled)": {
      // @ts-ignore: Object can be undefined error
      backgroundColor: theme.palette[colorName][100],
      // @ts-ignore: Object can be undefined error
      borderColor: theme.palette[colorName][700],
    },
    "&:active:not(:disabled)": {
      // @ts-ignore: Object can be undefined error
      backgroundColor: theme.palette[colorName][200],
    },
  });

const getTextColor =
  (colorName: keyof Palette) =>
  ({ theme }: { theme: Theme }) => ({
    "&:active:not(:disabled)": {
      // @ts-ignore: Object can be undefined error
      backgroundColor: theme.palette[colorName][100],
    },
  });

const BUTTON_THEME: Components<Theme>["MuiButton"] = {
  defaultProps: {
    disableRipple: true,
    disableElevation: true,
    variant: "outlined",
  },
  styleOverrides: {
    root: ({ theme }: { theme: Theme }) => ({
      transition: ["all ease-in-out 0.05s", "outline 0s"].join(", "),
      borderWidth: "2px !important",
      borderRadius: "999px",
      borderStyle: "solid",
      lineHeight: 1,
      outlineWidth: "2px",
      outlineStyle: "solid",
      outlineOffset: "2px",
      outlineColor: "transparent",
      whiteSpace: "nowrap",
      minWidth: "auto",
      "&:hover:not(:disabled), &:focus:not(:disabled)": {
        textDecoration: "none",
      },
      ":disabled": {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[600],
      },
      ":focus-visible": {
        outlineColor: theme.palette.primary[700],
        textDecoration: "none",
      },
      [`& > .${buttonClasses.startIcon}`]: {
        // Remove the negative margin that makes alignment hard
        marginLeft: 0,
      },
      [`& > .${buttonClasses.endIcon}`]: {
        // Remove the negative margin that makes alignment hard
        marginRight: 0,
      },
    }),
    // Variants x colors
    // contained -
    contained: {
      borderColor: "transparent",
    },
    containedPrimary: ({ theme }: { theme: Theme }) => ({
      ...getContainedColor("primary")({ theme }),
      backgroundColor: theme.palette.primary[700],
    }),
    containedSecondary: getContainedColor("secondary"),
    containedSuccess: getContainedColor("success"),
    containedError: getContainedColor("error"),
    containedInfo: getContainedColor("info"),
    containedWarning: getContainedColor("warning"),
    // outlined -
    outlined: ({ theme }: { theme: Theme }) => ({
      backgroundColor: theme.palette.common.white,
      ":disabled": {
        borderColor: "transparent",
      },
    }),
    outlinedPrimary: getOutlinedColor("primary"),
    outlinedSecondary: getOutlinedColor("secondary"),
    outlinedSuccess: getOutlinedColor("success"),
    outlinedError: getOutlinedColor("error"),
    outlinedInfo: getOutlinedColor("info"),
    outlinedWarning: getOutlinedColor("warning"),
    // text -
    text: {
      borderColor: "transparent",
    },
    textPrimary: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.primary[700],
      ...getTextColor("primary")({ theme }),
    }),
    textSecondary: getTextColor("secondary"),
    textSuccess: getTextColor("success"),
    textError: getTextColor("error"),
    textInfo: getTextColor("info"),
    textWarning: getTextColor("warning"),
    // link
    link: {
      // override root styles
      lineHeight: 1,
      fontWeight: 500,
      border: "none",
      borderRadius: 0,
      cursor: "pointer",
      "&:hover:not(:disabled), &:focus:not(:disabled)": {
        textDecoration: "underline",
        backgroundColor: "transparent",
      },
      "&:disabled": {
        backgroundColor: "transparent",
      },
    },
    linkPrimary: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.primary[700],
      backgroundColor: "transparent",
      // Match default outlined color border + alpha
      // This only matters in ButtonGroup
      // @ts-ignore
      borderColor: alpha(theme.palette.primary[500], 0.5),
    }),
    linkSecondary: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.secondary[700],
      backgroundColor: "transparent",
      // Match default outlined color border + alpha
      // This only matters in ButtonGroup
      // @ts-ignore
      borderColor: alpha(theme.palette.secondary[500], 0.5),
    }),
    linkSuccess: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.success[700],
      backgroundColor: "transparent",
      // Match default outlined color border + alpha
      // This only matters in ButtonGroup
      // @ts-ignore
      borderColor: alpha(theme.palette.success[500], 0.5),
    }),
    linkError: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.error[700],
      backgroundColor: "transparent",
      // Match default outlined color border + alpha
      // This only matters in ButtonGroup
      // @ts-ignore
      borderColor: alpha(theme.palette.error[500], 0.5),
    }),
    linkInfo: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.info[700],
      backgroundColor: "transparent",
      // Match default outlined color border + alpha
      // This only matters in ButtonGroup
      // @ts-ignore
      borderColor: alpha(theme.palette.info[500], 0.5),
    }),
    linkWarning: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.warning[700],
      backgroundColor: "transparent",
      // Match default outlined color border + alpha
      // This only matters in ButtonGroup
      // @ts-ignore
      borderColor: alpha(theme.palette.warning[500], 0.5),
    }),
    linkSizeSmall: {
      margin: 0,
      padding: 0,
    },
    linkSizeMedium: {
      margin: 0,
      padding: 0,
    },
    linkSizeLarge: {
      margin: 0,
      padding: 0,
    },
    linkSizeInherit: {
      margin: 0,
      padding: 0,
    },
    // Sizes
    sizeInherit: {
      fontSize: "inherit",
    },
    sizeSmall: ({ theme }: { theme: Theme }) => ({
      padding: theme.spacing(8, 18),
      fontSize: theme.typography.pxToRem(14),
    }),
    sizeMedium: ({ theme }: { theme: Theme }) => ({
      padding: theme.spacing(12, 28),
      fontSize: theme.typography.pxToRem(16),
    }),
    sizeLarge: ({ theme }: { theme: Theme }) => ({
      padding: theme.spacing(16, 36),
      fontSize: theme.typography.pxToRem(18),
    }),
  },
};

export default BUTTON_THEME;
